<template>
  <div>
    <div class="bg-white">
      <div class="flex justify-between items-center p-4 border-b border-gray-200">
        <h3 class="form-title">{{ editedItem ? 'Edit Plan' : 'Add Plan' }}</h3>
        <v-icon @click="cancel">mdi-close</v-icon>
      </div>
      <v-form ref="form" v-model="valid" lazy-validation class="p-4">
        <div class="p-4">
          <v-row class="grid grid-cols-2 gap-4">
            <v-col class="col">
              <v-text-field
                v-model="planName"
                outlined
                dense
                class="mb-2"
                label="Plan Name"
                :rules="[(v) => !!v || `Plan name is required`]"
                hide-details
              ></v-text-field>
            </v-col>
            <v-col>
              <v-text-field
                v-model="description"
                outlined
                dense
                class="mb-2"
                label="Plan Description"
                hide-details
              ></v-text-field>
            </v-col>

            <v-col>
              <v-select
                v-model="planDuration"
                label="Plan Duration"
                outlined
                :items="durationOptions"
                item-text="text"
                item-value="value"
                class="mb-2"
                dense
                hide-details
              ></v-select>
            </v-col>

            <v-col>
              <v-select
                v-model="paymentSchedule"
                label="Payment Cycle"
                outlined
                :items="paymentOptions"
                item-text="text"
                item-value="value"
                class="mb-2"
                dense
                hide-details
              ></v-select>
            </v-col>

            <v-col>
              <v-select
                v-model="serviceSchedule"
                label="Plan Schedule"
                outlined
                :items="scheduleOptions"
                item-text="text"
                item-value="value"
                class="mb-2"
                dense
                hide-details
              ></v-select>
            </v-col>

            <v-col>
              <v-text-field
                v-model="coverage"
                outlined
                dense
                label="Coverage"
                class="mb-2"
                hide-details
              ></v-text-field>
            </v-col>

            <v-col>
              <v-text-field
                v-model="mrp"
                outlined
                dense
                label="Plan price"
                class="mb-2"
                hide-details
              ></v-text-field>
            </v-col>

            <v-col>
              <v-text-field v-model="gst" outlined dense label="GST" hide-details></v-text-field>
            </v-col>

            <v-col>
              <v-text-field
                v-model="discount"
                outlined
                class="mb-2"
                dense
                label="Plan discount"
                hide-details
              ></v-text-field>
            </v-col>

            <v-col>
              <v-select
                v-model="status"
                label="Plan Status"
                outlined
                :items="statusOptions"
                item-text="text"
                item-value="value"
                class="mb-2"
                dense
                hide-details
              ></v-select>
            </v-col>
            <v-col>
              <v-text-field :value="finalPrice" outlined dense readonly hide-details></v-text-field>
            </v-col>
          </v-row>
        </div>
      </v-form>
      <div class="bg-white flex justify-end p-4 border-t border-gray-200">
        <v-btn outlined class="normal-case" @click="cancel">Cancel</v-btn>
        <v-btn
          class="ml-2 white--text normal-case"
          :color="$vuetify.theme.currentTheme.primary"
          @click="savePlan"
        >
          <v-icon left> mdi-content-save</v-icon>
          Save</v-btn
        >
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      planName: '',
      description: '',
      planDuration: 30,
      mrp: '',
      discount: '',
      gst: '',
      coverage: '',
      serviceSchedule: 7,
      paymentSchedule: 30,
      status: 'ACTIVE',
      valid: true,
      statusOptions: [
        { text: 'Active', value: 'ACTIVE' },
        { text: 'In Active', value: 'INACTIVE' }
      ],
      durationOptions: [
        { text: '1 Month', value: 30 },
        { text: '3 Months', value: 90 },
        { text: '6 Months', value: 180 },
        { text: '1 Year', value: 365 },
        { text: '2 Years', value: 730 },
        { text: '3 Years', value: 1095 },
        { text: '4 Years', value: 1460 },
        { text: '5 Years', value: 1825 }
      ],
      scheduleOptions: [
        { text: 'Weekly', value: 7 },
        { text: 'Monthly', value: 30 },
        { text: 'Quarterly', value: 90 },
        { text: 'Half Yearly', value: 180 },
        { text: 'Yearly', value: 365 }
      ],
      paymentOptions: [
        { text: 'Monthly', value: 30 },
        { text: 'Quarterly', value: 90 },
        { text: 'Half Yearly', value: 180 },
        { text: 'Yearly', value: 365 }
      ]
    }
  },
  props: {
    editedItem: {
      type: Object,
      default: null
    }
  },
  computed: {
    finalPrice() {
      const mrp = parseFloat(this.mrp) || 0
      const gst = parseFloat(this.gst) || 0
      const discount = parseFloat(this.discount) || 0

      return mrp + gst - discount
    }
  },
  watch: {
    editedItem: {
      handler(newItem) {
        if (newItem) {
          this.populateForm(newItem)
        } else {
          this.resetForm()
        }
      },
      immediate: true
    }
  },
  methods: {
    populateForm(item) {
      this.planName = item?.planName || ''
      this.description = item?.planDescription || ''
      this.planDuration = item?.planDuration || 30
      this.paymentSchedule = item?.paymentSchedule || 30
      this.serviceSchedule = item?.serviceSchedule || 7
      this.status = item?.status || 'ACTIVE'
      this.mrp = item?.priceBreakup?.mrp || ''
      this.gst = item?.priceBreakup?.gst || ''
      this.discount = item?.priceBreakup?.discount || ''
      this.coverage = item?.coverage?.[0] || ''
    },
    async savePlan() {
      try {
        const isValid = this.$refs.form.validate()
        if (!isValid) return
        const method = this.editedItem ? 'put' : 'post'
        const endpoint = this.editedItem ? `/ecom/amcplan/${this.editedItem._id}` : '/ecom/amcplan'
        const payload = {
          planName: this.planName,
          planDescription: this.description,
          planDuration: this.planDuration,
          serviceSchedule: this.serviceSchedule,
          paymentSchedule: this.paymentSchedule,
          coverage: [this.coverage], // Ensure coverage is an array
          status: this.status,
          priceBreakup: {
            mrp: this.mrp || 0,
            gst: this.gst || 0,
            discount: this.discount || 0
          },
          finalPrice: this.finalPrice
        }
        await this.$axios({
          method: method,
          url: endpoint,
          data: payload
        })
        this.$emit('saved') // Emit saved event
        this.resetForm() // Clear the form fields
        // Let the parent component handle dialog closing
      } catch (error) {
        console.error('Error:', error)
        const errorMessage = error.response?.data?.message || error.message || 'An error occurred'
        this.$toast.error(errorMessage)
      }
    },
    cancel() {
      this.$emit('cancel')
    },
    resetForm() {
      // Only reset form fields if we're not editing an item
      if (!this.editedItem) {
        // Reset all form fields to their initial values
        this.planName = ''
        this.description = ''
        this.planDuration = 30
        this.mrp = ''
        this.discount = ''
        this.gst = ''
        this.coverage = ''
        this.serviceSchedule = 7
        this.paymentSchedule = 30
        this.status = 'ACTIVE'
      }
      // Reset form validation
      if (this.$refs.form) {
        this.$refs.form.reset()
      }
    }
  }
}
</script>
<style scoped>
.col {
  padding: 0;
}
.form-title {
  font-size: 1.25rem;
  padding: 0px !important;
  font-weight: 600;
  color: #21427d;
  margin: 0;
}
</style>
